import api from '@/services/api'
import { can } from '@/config/permissions'
import { getDraft, putDraft, ownerKey } from './drafts'
import { localDay, online } from './runtime'
import { reportPwaError } from './diagnostics'

/** Permission (F code, action G) needed to send a draft of this kind. */
export const draftPermission = d => d.kind === 'task' ? 'F7' : 'F4'

/** Body and headers of a draft's request; identical on every replay. */
export function draftRequest(d) {
  const headers = { 'Idempotency-Key': d.key }
  if (d.kind !== 'alert') return { body: d.payload, headers }
  const body = new FormData()
  Object.entries(d.payload).forEach(([k,v]) => { if (v != null && v !== '') body.append(k,v) })
  if (d.file) body.append('file', d.file, d.fileName || 'photo.jpg')
  headers['Content-Type'] = 'multipart/form-data'
  return { body, headers }
}

// All retries use exactly the original payload/key after an uncertain outcome.
const inflight = new Map()
// Cùng một nháp chỉ có một lần gửi đang chạy; lần gọi đồng thời dùng chung kết quả.
export function sendDraft(id) {
  if (inflight.has(id)) return inflight.get(id)
  const p = sendDraftOnce(id).finally(() => inflight.delete(id))
  inflight.set(id, p)
  return p
}
async function sendDraftOnce(id) {
  const d = await getDraft(id)
  if (!d) throw Error('Không tìm thấy bản nháp của tài khoản này.')
  if (d.schema !== 1) throw Error('Bản nháp dùng cấu trúc chưa được hỗ trợ. Vui lòng cập nhật ứng dụng trước khi gửi.')
  if (d.status === 'sent') return d.result
  if (!online.value) throw Error('Chưa có kết nối. Bản nháp vẫn được giữ trên máy.')
  const identity = ownerKey()
  const me = (await api.get('/me')).data.data
  if (!me || `${me.farm_id}:${me.id}` !== identity) throw Error('Tài khoản đã thay đổi. Vui lòng đăng nhập lại.')
  // /me của bản backend cũ chưa trả permissions: dùng quyền lưu lúc đăng nhập, server vẫn kiểm tra lại khi nhận.
  let perms = me.permissions
  if (!perms) { try { perms = JSON.parse(localStorage.getItem('user') || '{}').permissions } catch { perms = null } }
  if (!can(perms, draftPermission(d), 'G')) throw Error('Bạn không còn quyền gửi bản nháp này. Vui lòng liên hệ quản lý.')
  // The server always enforces the current permission before replaying.
  if (!['uncertain','sending'].includes(d.status)) {
    if (d.date !== localDay()) throw Error('Nháp thuộc ngày khác. Hãy mở lại đối tượng và kiểm tra trước khi tạo lượt mới.')
    // Feeding drafts are checked against the few rows they depend on
    // (/feeding/assessments/state), not the whole farm's board.
    const feedState = (params) => api.get('/feeding/assessments/state', { params }).then((r) => r.data.data || {})
    if (d.kind === 'assessment') {
      const { sow } = await feedState({ sow_id: d.payload.sow_id })
      if (!sow || (sow.pen_id || null) !== (d.penId || null) || (sow.last_assessed_at || null) !== d.baseline) throw Error('Vị trí hoặc trạng thái nái đã thay đổi. Vui lòng kiểm tra lại.')
    }
    if (d.kind === 'task') {
      const items = (await api.get('/daily-tasks', { params: { date: d.date } })).data.data || []
      const task = items.find(t => t.id === d.entityId)
      if (!task || !task.is_active || task.done || task.done_count !== d.doneCount) throw Error('Công việc đã thay đổi hoặc đã được ghi nhận. Vui lòng kiểm tra lại.')
    }
    if (d.kind === 'pen-feeding' || d.kind === 'alert') {
      const withSow = d.kind === 'alert' && d.payload.sow_id
      const { pen, sow } = await feedState({ pen_id: d.penId, ...(withSow ? { sow_id: d.payload.sow_id } : {}) })
      if (d.kind === 'pen-feeding' && (pen?.last_at || null) !== d.baseline) throw Error('Lượt cho ăn đã thay đổi. Vui lòng kiểm tra lại.')
      if (!pen || (d.barnId && pen.barn_id !== d.barnId)) throw Error('Ô chuồng đã thay đổi. Vui lòng kiểm tra lại.')
      if (withSow && sow?.pen_id !== d.penId) throw Error('Nái đã thay đổi ô chuồng. Vui lòng kiểm tra lại cảnh báo trước khi gửi.')
    }
  }
  if (identity !== ownerKey()) throw Error('Tài khoản đã thay đổi.')
  await putDraft({ ...d, status: 'sending' })
  const { body, headers } = draftRequest(d)
  let result
  try {
    result = (await api.post(d.endpoint, body, { headers })).data.data
  } catch(e) {
    reportPwaError('SF_DRAFT_SEND_FAILED')
    const status = !e.response || e.response.status >= 500 ? 'uncertain' : 'draft'
    await putDraft({ ...d, status, error: e.message })
    throw e
  }
  // If local persistence fails after the response, retain the same key for replay.
  await putDraft({ ...d, status: 'sent', result, file: null, error: '' })
  return result
}

